import React, { useRef } from 'react';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';

// BL-177 Step 2j (E): the 3D segmented switch. A recessed track with ONE raised knob that slides between 2-4 positions
// (Busy / Limited / Full). It is the multi-position sibling of ui/Switch and follows the same 3D pressable standard:
// pressing sinks the knob 3 px, disabled is flat at 45%.
//
//   <SegmentedSwitch3D label="What colleagues can see" value="limited" onChange={setLevel}
//        options={[{ value: 'busy', label: 'Busy' }, { value: 'limited', label: 'Limited' }, { value: 'full', label: 'Full' }]} />
//
// Accessibility: role="radiogroup" with one role="radio" button per position (roving tabindex: the chosen one is the tab stop).
// Arrow keys (Left/Up = previous, Right/Down = next, Home, End) move AND choose, like native radios. Space/Enter choose the focused one.
// State is never colour-only: the chosen label is bold and sits on the raised knob.
// Reduced motion: the knob does not slide (class ia-seg3d--still, and the CSS media query as a second line).
// `describedBy` points at the help text that explains the positions. Styles: src/styles/settingsNav.css.
export default function SegmentedSwitch3D({
    options,
    value,
    onChange,
    label,
    labelledBy,
    describedBy,
    disabled = false,
    className = '',
    id,
    testId = 'segmented-switch',
}) {
    const reduced = usePrefersReducedMotion();
    const refs = useRef([]);
    const list = Array.isArray(options) ? options : [];
    const count = list.length || 1;
    const selectedIndex = list.findIndex((option) => option.value === value);
    // Before a value is known the knob rests on the first position, but nothing is announced as chosen.
    const knobIndex = selectedIndex >= 0 ? selectedIndex : 0;
    const tabStop = selectedIndex >= 0 ? selectedIndex : 0;

    const choose = (index) => {
        const option = list[index];
        if (!option || disabled) return;
        if (option.value !== value && onChange) onChange(option.value);
        const node = refs.current[index];
        if (node && typeof node.focus === 'function') node.focus();
    };

    const onKeyDown = (event, index) => {
        if (disabled) return;
        let next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % count;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + count) % count;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = count - 1;
        if (next === null) return;
        event.preventDefault();
        choose(next);
    };

    return (
        <div
            id={id}
            role="radiogroup"
            aria-label={labelledBy ? undefined : label}
            aria-labelledby={labelledBy || undefined}
            aria-describedby={describedBy || undefined}
            aria-disabled={disabled || undefined}
            data-ia-seg3d={selectedIndex >= 0 ? list[selectedIndex].value : 'none'}
            data-testid={testId}
            className={`ia-seg3d ${reduced ? 'ia-seg3d--still' : ''} ${disabled ? 'ia-seg3d--disabled' : ''} ${className}`}
            style={{ '--seg-count': count, '--seg-index': knobIndex }}
        >
            <span aria-hidden="true" className="ia-seg3d__knob" data-seg3d-knob="true" />
            {list.map((option, index) => {
                const checked = index === selectedIndex;
                return (
                    <button
                        key={option.value}
                        ref={(node) => { refs.current[index] = node; }}
                        type="button"
                        role="radio"
                        aria-checked={checked}
                        tabIndex={index === tabStop ? 0 : -1}
                        disabled={disabled}
                        data-seg3d-option={option.value}
                        className={`ia-seg3d__option ${checked ? 'is-chosen' : ''}`}
                        onClick={() => choose(index)}
                        onKeyDown={(event) => onKeyDown(event, index)}
                    >
                        <span className="ia-seg3d__label">{option.label}</span>
                    </button>
                );
            })}
        </div>
    );
}
